import { configWithAuth } from '../helpers'
import { makeGetRequest, makePostRequest, makePutRequest, makeDeleteRequest } from '../helpers/api'

// Course statistics endpoints exist in an organization scoped and a classroom scoped variant that
// differ only by prefix, so the callers pass an optional userGroupId rather than picking a service.
const courseStatsBasePath = (payload) => {
    const base = 'admin/organization/' + payload.organizationId
    return payload.userGroupId
        ? base + '/user/group/' + payload.userGroupId + '/stats/course/group/v2'
        : base + '/stats/course/group/v2'
}

const courseStatsDateQuery = (payload) => {
    const parts = []
    if (payload.fromDate) {
        parts.push('fromDate=' + encodeURIComponent(payload.fromDate))
    }
    if (payload.toDate) {
        parts.push('toDate=' + encodeURIComponent(payload.toDate))
    }
    return parts.length > 0 ? '?' + parts.join('&') : ''
}

const courseStatsOverviewQuery = (payload) => {
    const dateQuery = courseStatsDateQuery(payload)
    if (!payload.courseGroupIds || payload.courseGroupIds.length === 0) {
        return dateQuery
    }
    const courseGroupPart = 'courseGroupIds=' + encodeURIComponent(payload.courseGroupIds.join(','))
    return dateQuery ? dateQuery + '&' + courseGroupPart : '?' + courseGroupPart
}

export const organizationService = {
    createSchool: (payload) => makePostRequest('admin/organization', JSON.stringify(payload), configWithAuth()),
    getMyOrganizations: () => makeGetRequest('admin/organization/list', configWithAuth()),
    setOrganizationInfo: (payload) => makePutRequest('admin/organization', JSON.stringify(payload), configWithAuth()),
    inviteStudentsToSchool: (payload) =>
        makePostRequest('admin/organization/user', JSON.stringify(payload), configWithAuth()),
    getOrganizationUsers: (organizationId) =>
        makeGetRequest('admin/organization/' + organizationId + '/users', configWithAuth()),
    getInviteRolesAvailable: (payload) =>
        makeGetRequest('admin/organization/' + payload.organizationId + '/user/give/roles', configWithAuth()),
    getAllUserGroups: (organizationId) =>
        makeGetRequest('admin/usergroup/organization/' + organizationId, configWithAuth()),
    addUserGroupUser: (payload) => makePostRequest('admin/usergroup/user', JSON.stringify(payload), configWithAuth()),
    addUserGroupTopic: (payload) => makePostRequest('admin/usergroup/topic', JSON.stringify(payload), configWithAuth()),
    deleteUserGroupTopic: (payload) =>
        makeDeleteRequest('admin/usergroup/topic', JSON.stringify(payload), configWithAuth()),
    deleteOrganizationUser: (payload) =>
        makeDeleteRequest(
            'admin/organization/' + payload.organizationId + '/user/' + payload.userId,
            null,
            configWithAuth()
        ),
    getOrganizationFileAsset: (payload) =>
        makeGetRequest('admin/organization/' + payload.organizationId + '/assets', configWithAuth()),
    updateOrganizationFileAsset: (payload) =>
        makePostRequest(
            'admin/organization/' + payload.organizationId + '/asset',
            JSON.stringify(payload),
            configWithAuth()
        ),
    deleteOrganizationFileAsset: (payload) =>
        makeDeleteRequest(
            'admin/organization/' + payload.organizationId + '/delete/asset/' + payload.organizationFileAssetId,
            null,
            configWithAuth()
        ),
    excelExportFromTemplateOrgStats: (payload) =>
        makeGetRequest(
            'admin/organization/' + payload.organizationId + '/excel/template/metadata?useBase64=true',
            configWithAuth()
        ),
    excelExportFromTemplateClasssoomStats: (payload) =>
        makeGetRequest(
            'admin/organization/' +
                payload.organizationId +
                '/excel/usergroup/' +
                payload.classroomId +
                '/template/metadata?useBase64=true',
            configWithAuth()
        ),
    exportCourseGroupProgressionOrg: (payload) =>
        makePostRequest(
            'admin/organization/' +
                payload.organizationId +
                '/excel/coursegroup-progression/organization?useBase64=true&languageId=' +
                (payload.languageId || 1),
            JSON.stringify({
                courseGroupIds: payload.courseGroupIds || [],
                fromDate: payload.fromDate || null,
                toDate: payload.toDate || null,
                excludedUserGroupIds: payload.excludedUserGroupIds || [],
                includePassedColumn: payload.columnOptions?.includePassed !== false,
                includeClassroomColumn: payload.columnOptions?.includeClassroom === true,
            }),
            configWithAuth()
        ),
    exportCourseGroupProgressionStudent: (payload) => {
        let url =
            'admin/organization/' +
            payload.organizationId +
            '/excel/coursegroup-progression/user/' +
            payload.userId +
            '?useBase64=true&languageId=' +
            (payload.languageId || 1)
        if (payload.fromDate) url += '&fromDate=' + encodeURIComponent(payload.fromDate)
        if (payload.toDate) url += '&toDate=' + encodeURIComponent(payload.toDate)
        return makeGetRequest(url, configWithAuth())
    },
    exportCourseGroupProgressionClassroom: (payload) =>
        makePostRequest(
            'admin/organization/' +
                payload.organizationId +
                '/excel/coursegroup-progression?useBase64=true&languageId=' +
                (payload.languageId || 1),
            JSON.stringify({
                courseGroupIds: payload.courseGroupIds,
                userGroupId: payload.userGroupId,
                fromDate: payload.fromDate || null,
                toDate: payload.toDate || null,
                includePassedColumn: payload.columnOptions?.includePassed !== false,
                includeClassroomColumn: payload.columnOptions?.includeClassroom === true,
            }),
            configWithAuth()
        ),
    getAvailableCourseGroups: (organizationId) =>
        makeGetRequest('admin/course/group/available/organization/' + organizationId, configWithAuth()),

    // graph data
    getGraphDataHourlyOrganization: (payload) =>
        makeGetRequest('admin/organization/' + payload.organizationId + '/stats/graph/hourly', configWithAuth()),
    getGraphDataHourlyUserGroup: (payload) =>
        makeGetRequest(
            'admin/organization/' +
                payload.organizationId +
                '/user/group/' +
                payload.userGroupId +
                '/stats/graph/hourly',
            configWithAuth()
        ),

    getGraphDataDayOfWeekOrganization: (payload) =>
        makeGetRequest('admin/organization/' + payload.organizationId + '/stats/graph/dayofweek', configWithAuth()),
    getGraphDataDayOfWeekUserGroup: (payload) =>
        makeGetRequest(
            'admin/organization/' +
                payload.organizationId +
                '/user/group/' +
                payload.userGroupId +
                '/stats/graph/dayofweek',
            configWithAuth()
        ),

    getGraphDataDailyUsageAggregatedOrganization: (payload) =>
        makeGetRequest(
            'admin/organization/' + payload.organizationId + '/stats/graph/daily/usage/aggregate',
            configWithAuth()
        ),
    getGraphDataDailyUsageAggregatedUserGroup: (payload) =>
        makeGetRequest(
            'admin/organization/' +
                payload.organizationId +
                '/user/group/' +
                payload.userGroupId +
                '/stats/graph/daily/usage/aggregate',
            configWithAuth()
        ),

    getGraphDataDailyLessonAggregatedOrganization: (payload) =>
        makeGetRequest(
            'admin/organization/' + payload.organizationId + '/stats/graph/daily/lesson/aggregate',
            configWithAuth()
        ),
    getGraphDataDailyLessonAggregatedUserGroup: (payload) =>
        makeGetRequest(
            'admin/organization/' +
                payload.organizationId +
                '/user/group/' +
                payload.userGroupId +
                '/stats/graph/daily/lesson/aggregate',
            configWithAuth()
        ),

    getOrganizationFactBoxStats: (payload) =>
        makeGetRequest('admin/organization/' + payload.organizationId + '/stats', configWithAuth()),
    getOrganizationCourseGroupUsage: (payload) =>
        makeGetRequest('admin/organization/' + payload.organizationId + '/stats/course/group', configWithAuth()),

    // course statistics v2 - per course usage, adoption and quality, plus the user filter.
    // The organization and classroom variants share a path suffix, so scope is only ever a prefix.
    getCourseStatsOverview: (payload) =>
        makeGetRequest(
            courseStatsBasePath(payload) + '/overview' + courseStatsOverviewQuery(payload),
            configWithAuth()
        ),
    getCourseStatsTopicTrend: (payload) =>
        makeGetRequest(
            courseStatsBasePath(payload) +
                '/topic/' +
                payload.topicId +
                '/trend' +
                courseStatsDateQuery(payload),
            configWithAuth()
        ),
    getCourseStatsFilterPresets: (payload) =>
        makeGetRequest(
            'admin/organization/' + payload.organizationId + '/stats/course/group/v2/filter/presets',
            configWithAuth()
        ),
    // Courses and topics for the find users pickers. Independent of the scope bar and the date
    // range, so it is fetched once per scope rather than alongside the overview.
    getCourseStatsCatalogue: (payload) =>
        makeGetRequest(courseStatsBasePath(payload) + '/catalogue', configWithAuth()),
    postCourseStatsFilter: (payload) =>
        makePostRequest(
            courseStatsBasePath(payload) + '/filter',
            JSON.stringify(payload.filterRequestDto),
            configWithAuth()
        ),
    exportCourseStatsFilter: (payload) =>
        makePostRequest(
            courseStatsBasePath(payload) + '/filter/export?useBase64=true',
            JSON.stringify(payload.filterRequestDto),
            configWithAuth()
        ),

    // time zone location endpoints
    getOrganizationTimeZoneLocations: (payload) =>
        makeGetRequest('admin/organization/' + payload.organizationId + '/time/zone/list', configWithAuth()),
    setOrganizationTimeZoneLocations: (payload) =>
        makePostRequest(
            'admin/organization/' + payload.organizationId + '/time/zone',
            JSON.stringify(payload),
            configWithAuth()
        ),

    // organization course groups
    getOrganizationCourseGroups: (payload) =>
        makeGetRequest('admin/organization/' + payload.organizationId + '/course/group', configWithAuth()),
    addCourseGroupToOrganization: (payload) =>
        makePostRequest(
            'admin/organization/' + payload.organizationId + '/course/group/' + payload.courseGroupId,
            null,
            configWithAuth()
        ),
    removeCourseGroupFromOrganization: (payload) =>
        makeDeleteRequest(
            'admin/organization/' + payload.organizationId + '/course/group/' + payload.courseGroupId,
            null,
            configWithAuth()
        ),

    // organziation information
    updateOrganizationInformation: (payload) =>
        makePostRequest(
            'admin/organization/' + payload.organizationId + '/information',
            JSON.stringify(payload.updateOrganizationInformationDto),
            configWithAuth()
        ),

    // skills to show
    getSkillsCourseGroups: (payload) =>
        makeGetRequest('admin/course/group/skills/organization/' + payload.organizationId, configWithAuth()),

    downloadUserImportTemplate: (organizationId, languageId) =>
        makeGetRequest(
            'admin/organization/' + organizationId + '/user-import/template?languageId=' + (languageId || 1),
            { ...configWithAuth(), responseType: 'blob' }
        ),
    uploadUserImport: (organizationId, file) => {
        const formData = new FormData()
        formData.append('file', file)
        return makePostRequest(
            'admin/organization/' + organizationId + '/user-import/upload',
            formData,
            configWithAuth('multipart/form-data')
        )
    },

    previewOrganizationEmail: (organizationId, filters) =>
        makePostRequest(
            'admin/organization/' + organizationId + '/email/preview',
            JSON.stringify(filters),
            configWithAuth()
        ),
    sendOrganizationEmail: (organizationId, payload) =>
        makePostRequest(
            'admin/organization/' + organizationId + '/email/send',
            JSON.stringify(payload),
            configWithAuth()
        ),
}
